'use client';

import Icon from '@/components/ui/AppIcon';

interface PriceBreakdownData {
  subtotal: number;
  deliveryCharges: number;
  tax: number;
  discount: number;
  total: number;
}

interface PriceBreakdownProps {
  breakdown: PriceBreakdownData;
  estimatedDelivery: string;
}

const PriceBreakdown = ({ breakdown, estimatedDelivery }: PriceBreakdownProps) => {
  return (
    <div className="bg-card border border-border rounded-md p-6 sticky top-[140px]">
      <h2 className="font-heading font-bold text-xl text-foreground mb-4">
        Price Details
      </h2>

      <div className="space-y-3 mb-4 pb-4 border-b border-border">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Subtotal</span>
          <span className="font-data font-medium text-sm text-foreground">
            ₹{breakdown.subtotal.toFixed(2)}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Delivery Charges</span>
          <span className="font-data font-medium text-sm text-foreground">
            {breakdown.deliveryCharges === 0 ? (
              <span className="text-success">FREE</span>
            ) : (
              `₹${breakdown.deliveryCharges.toFixed(2)}`
            )}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Tax (GST 5%)</span>
          <span className="font-data font-medium text-sm text-foreground">
            ₹{breakdown.tax.toFixed(2)}
          </span>
        </div>

        {breakdown.discount > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-success">Discount Applied</span>
            <span className="font-data font-medium text-sm text-success">
              -₹{breakdown.discount.toFixed(2)}
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-6 pb-6 border-b border-border">
        <span className="font-heading font-bold text-lg text-foreground">Total Amount</span>
        <span className="font-heading font-bold text-2xl text-primary">
          ₹{breakdown.total.toFixed(2)}
        </span>
      </div>

      <div className="bg-muted rounded-md p-4 mb-4">
        <div className="flex items-start gap-3">
          <Icon name="TruckIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-sm text-foreground mb-1">
              Estimated Delivery
            </div>
            <div className="text-sm text-muted-foreground">
              {estimatedDelivery}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-success/10 border border-success/20 rounded-md p-3 mb-4">
        <div className="flex items-center gap-2 text-success">
          <Icon name="CheckCircleIcon" size={16} />
          <span className="text-xs font-medium">
            You're saving ₹{breakdown.discount.toFixed(2)} on this order
          </span>
        </div>
      </div>

      <button className="w-full bg-primary text-primary-foreground py-3 rounded-md font-heading font-semibold text-base transition-smooth hover:shadow-elevation-md hover-lift focus-ring">
        Proceed to Checkout
      </button>

      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Icon name="ShieldCheckIcon" size={16} />
        <span>100% Secure Payment</span>
      </div>
    </div>
  );
};

export default PriceBreakdown;